import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import MapView from "../components/MapView";
import { Plus, Pencil, Trash2, MapPin, Phone, Clock, Building2, AlertTriangle, X, Search, LocateFixed, Loader2 } from "lucide-react";

const KIND = { fornitore: "Fornitore", cliente: "Cliente", sede: "Sede aziendale" };
const EMPTY_LOC = () => ({ id: crypto.randomUUID(), label: "", address: "", lat: "", lng: "", contact_name: "", contact_phone: "", opening_hours: "", access_notes: "" });

export default function Suppliers() {
  const { user } = useAuth();
  const canWrite = ["pm_produzione", "responsabile_logistica", "amministratore"].includes(user?.role);
  const canDelete = ["responsabile_logistica", "amministratore"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [dupWarn, setDupWarn] = useState([]);
  const [q, setQ] = useState("");
  const [geo, setGeo] = useState(null);

  const load = () => api.get("/suppliers").then((r) => setRows(r.data)).catch(() => {});
  const loadGeo = useCallback(async () => {
    if (!canDelete) return;
    try { const { data } = await api.get("/suppliers/geocode-status"); setGeo(data); return data; } catch { return null; }
  }, [canDelete]);
  useEffect(() => { load(); }, []);
  useEffect(() => { loadGeo(); }, [loadGeo]);
  useEffect(() => {
    if (!geo?.job?.running) return;
    const t = setInterval(async () => { const d = await loadGeo(); if (!d?.job?.running) { clearInterval(t); load(); } }, 4000);
    return () => clearInterval(t);
  }, [geo?.job?.running, loadGeo]);

  const startGeocode = async () => {
    try { await api.post("/suppliers/geocode-batch"); toast.success("Geocodifica avviata in background"); loadGeo(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const openNew = () => { setEditing({ name: "", kind: "fornitore", notes: "", locations: [EMPTY_LOC()] }); setDupWarn([]); setOpen(true); };
  const openEdit = (s) => { setEditing({ ...s, locations: (s.locations || []).map((l) => ({ ...l, lat: l.lat ?? "", lng: l.lng ?? "" })) }); setDupWarn([]); setOpen(true); };

  const checkDup = async (name) => {
    if (!name || editing?.id) return;
    try { const { data } = await api.get(`/suppliers/duplicates?name=${encodeURIComponent(name)}`); setDupWarn(data.possible_duplicates || []); } catch {}
  };

  const save = async () => {
    const body = { ...editing, locations: editing.locations.map((l) => ({ ...l, lat: l.lat === "" ? null : parseFloat(l.lat), lng: l.lng === "" ? null : parseFloat(l.lng) })) };
    try {
      if (editing.id) await api.put(`/suppliers/${editing.id}`, body);
      else await api.post("/suppliers", body);
      toast.success("Anagrafica salvata"); setOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (s) => { if (!window.confirm(`Eliminare ${s.name}?`)) return; try { await api.delete(`/suppliers/${s.id}`); toast.success("Eliminato"); load(); } catch (e) { toast.error(apiErr(e)); } };

  const setLoc = (i, k, v) => setEditing((e) => { const locations = [...e.locations]; locations[i] = { ...locations[i], [k]: v }; return { ...e, locations }; });

  const ql = q.trim().toLowerCase();
  const filtered = ql
    ? rows.filter((s) => (s.name || "").toLowerCase().includes(ql) || (s.notes || "").toLowerCase().includes(ql)
        || (s.locations || []).some((l) => (l.address || "").toLowerCase().includes(ql)))
    : rows;
  const LIMIT = 60;
  const shown = filtered.slice(0, LIMIT);
  const markers = rows.flatMap((s) => (s.locations || []).filter((l) => l.lat != null).map((l) => ({
    lat: l.lat, lng: l.lng, title: s.name, subtitle: l.address,
    color: s.kind === "cliente" ? "#8C4A32" : s.kind === "sede" ? "#3D271D" : "#B88E4C", label: (s.name || "?")[0],
  })));

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div><h1 className="font-display text-3xl text-lm-text">Anagrafica</h1><p className="text-sm text-lm-muted">Fornitori, clienti e sedi · {rows.length} schede</p></div>
        {canWrite && <Button data-testid="new-supplier-btn" onClick={openNew} className="bg-lm-brown hover:bg-lm-brownh text-lm-cream"><Plus className="w-4 h-4 mr-2" /> Nuova scheda</Button>}
      </div>

      {canDelete && geo && (geo.missing > 0 || geo.job?.running) && (
        <div className="lm-card p-4 flex flex-wrap items-center justify-between gap-3" data-testid="geocode-panel">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-lm-brown flex items-center gap-1.5"><LocateFixed className="w-4 h-4" /> Coordinate GPS</p>
            {geo.job?.running ? (
              <div className="mt-1">
                <p className="text-xs text-lm-text2" data-testid="geocode-progress">Geocodifica in corso: {geo.job.done || 0}/{geo.job.total || 0} indirizzi ({geo.job.found || 0} trovati)</p>
                <div className="mt-1 h-2 w-64 max-w-full bg-lm-sandsoft rounded-full overflow-hidden">
                  <div className="h-full bg-lm-brown transition-all" style={{ width: `${geo.job.total ? Math.round((geo.job.done / geo.job.total) * 100) : 0}%` }} />
                </div>
              </div>
            ) : (
              <p className="text-xs text-lm-text2 mt-0.5">{geo.missing} indirizzi senza coordinate: senza GPS non compaiono su mappa e percorsi.</p>
            )}
          </div>
          {geo.job?.running
            ? <Button disabled variant="outline" className="border-lm-sand text-lm-brown"><Loader2 className="w-4 h-4 mr-2 animate-spin" /> In corso…</Button>
            : <Button data-testid="geocode-btn" onClick={startGeocode} className="bg-lm-brown hover:bg-lm-brownh text-lm-cream"><LocateFixed className="w-4 h-4 mr-2" /> Geocodifica GPS mancanti</Button>}
        </div>
      )}

      {markers.length > 0 && (
        <div className="lm-card p-4">
          <h2 className="font-display text-lg text-lm-brown mb-3 flex items-center gap-2"><MapPin className="w-5 h-5" /> Mappa destinazioni ({markers.length})</h2>
          <MapView markers={markers} height={360} testid="suppliers-map" />
        </div>
      )}

      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-lm-muted" />
        <Input data-testid="supplier-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome, città o email…" className="pl-9 bg-white" />
      </div>
      <p className="text-xs text-lm-muted">{filtered.length} schede{ql ? " trovate" : ""}{filtered.length > LIMIT ? ` · mostro le prime ${LIMIT}, affina la ricerca` : ""}</p>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {shown.map((s) => (
          <div key={s.id} data-testid={`supplier-${s.id}`} className="lm-card p-5">
            <div className="flex items-start justify-between">
              <div>
                <span className="text-[10px] uppercase tracking-wider font-bold text-lm-muted">{KIND[s.kind]}</span>
                <h3 className="font-display text-xl text-lm-brown">{s.name}</h3>
              </div>
              <Building2 className="w-5 h-5 text-lm-sand" />
            </div>
            {s.notes && <p className="text-sm text-lm-text2 mt-1">{s.notes}</p>}
            <div className="mt-3 space-y-2">
              {(s.locations || []).map((l) => (
                <div key={l.id} className="bg-lm-sandsoft/40 rounded-lg p-3 text-sm">
                  <p className="font-semibold text-lm-text">{l.label}</p>
                  <p className="text-lm-text2 flex items-center gap-1.5 mt-0.5"><MapPin className="w-3.5 h-3.5 shrink-0" /> {l.address} {l.lat == null && <span className="text-[#9A6B00]">(no GPS)</span>}</p>
                  {l.contact_phone && <p className="text-lm-muted flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> {l.contact_phone} {l.contact_name ? `· ${l.contact_name}` : ""}</p>}
                  {l.opening_hours && <p className="text-lm-muted flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {l.opening_hours}</p>}
                  {l.access_notes && <p className="text-xs text-lm-muted mt-1 italic">{l.access_notes}</p>}
                </div>
              ))}
            </div>
            {canWrite && (
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => openEdit(s)} className="border-lm-sand text-lm-brown"><Pencil className="w-4 h-4 mr-1.5" /> Modifica</Button>
                {canDelete && <Button size="sm" variant="outline" onClick={() => del(s)} className="border-lm-sand text-[#991B1B]"><Trash2 className="w-4 h-4" /></Button>}
              </div>
            )}
          </div>
        ))}
      </div>

      {open && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto bg-lm-cream" data-testid="supplier-dialog">
            <DialogHeader><DialogTitle className="font-display text-2xl text-lm-brown">{editing.id ? "Modifica scheda" : "Nuova scheda"}</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label className="text-xs text-lm-text2">Nome</Label><Input data-testid="sup-name" value={editing.name} onChange={(e) => setEditing((x) => ({ ...x, name: e.target.value }))} onBlur={(e) => checkDup(e.target.value)} className="mt-1 bg-white" /></div>
                <div><Label className="text-xs text-lm-text2">Tipo</Label>
                  <Select value={editing.kind} onValueChange={(v) => setEditing((x) => ({ ...x, kind: v }))}>
                    <SelectTrigger className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(KIND).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              {dupWarn.length > 0 && (
                <div className="flex items-start gap-2 text-xs text-[#9A6B00] bg-[#FEF8EC] border border-[#F3D295] rounded-lg px-3 py-2" data-testid="dup-warn">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>Possibile duplicato: {dupWarn.map((d) => d.name).join(", ")}. Verifica prima di creare una nuova scheda.</span>
                </div>
              )}
              <div><Label className="text-xs text-lm-text2">Note</Label><Textarea value={editing.notes || ""} onChange={(e) => setEditing((x) => ({ ...x, notes: e.target.value }))} className="mt-1 bg-white" rows={2} /></div>

              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-wider font-bold text-lm-muted">Sedi ({editing.locations.length})</p>
                <Button size="sm" variant="outline" onClick={() => setEditing((x) => ({ ...x, locations: [...x.locations, EMPTY_LOC()] }))} className="border-lm-sand text-lm-brown"><Plus className="w-4 h-4 mr-1" /> Sede</Button>
              </div>
              {editing.locations.map((l, i) => (
                <div key={l.id} className="border border-lm-sandsoft rounded-xl p-3 bg-white space-y-2 relative">
                  {editing.locations.length > 1 && <button onClick={() => setEditing((x) => ({ ...x, locations: x.locations.filter((_, j) => j !== i) }))} className="absolute top-2 right-2 text-lm-muted"><X className="w-4 h-4" /></button>}
                  <div className="grid sm:grid-cols-2 gap-2">
                    <Input placeholder="Etichetta (es. Magazzino)" value={l.label} onChange={(e) => setLoc(i, "label", e.target.value)} />
                    <Input placeholder="Indirizzo completo" value={l.address} onChange={(e) => setLoc(i, "address", e.target.value)} />
                    <Input placeholder="Lat (es. 45.68)" value={l.lat} onChange={(e) => setLoc(i, "lat", e.target.value)} />
                    <Input placeholder="Lng (es. 9.15)" value={l.lng} onChange={(e) => setLoc(i, "lng", e.target.value)} />
                    <Input placeholder="Referente" value={l.contact_name} onChange={(e) => setLoc(i, "contact_name", e.target.value)} />
                    <Input placeholder="Telefono" value={l.contact_phone} onChange={(e) => setLoc(i, "contact_phone", e.target.value)} />
                    <Input placeholder="Orari apertura" value={l.opening_hours} onChange={(e) => setLoc(i, "opening_hours", e.target.value)} />
                    <Input placeholder="Indicazioni accesso" value={l.access_notes} onChange={(e) => setLoc(i, "access_notes", e.target.value)} />
                  </div>
                </div>
              ))}
              <p className="text-[11px] text-lm-muted">Suggerimento: inserisci le coordinate GPS (lat/lng) per abilitare mappa e calcolo automatico dei percorsi stradali.</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} className="border-lm-sand text-lm-brown">Annulla</Button>
              <Button data-testid="save-supplier-btn" onClick={save} className="bg-lm-brown hover:bg-lm-brownh text-lm-cream">Salva</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
